import { useState, useEffect, useCallback, useMemo, memo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  Handle,
  Position,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft, Save, Play, Zap, GitBranch, GitMerge, Clock, 
  Square, Plus, Trash2, Settings, CheckCircle, Calendar, Timer,
  Repeat, AlertTriangle, RefreshCw, Sparkles, Send, Mail,
  Database, Globe, MessageSquare, FileText, ShieldAlert, Cpu, History
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import TriggerNodeConfig from "@/components/TriggerNodeConfig";
import GetValueConfig from "@/components/GetValueConfig";
import SetValueConfig from "@/components/SetValueConfig";
import GmailTriggerConfig from "@/components/GmailTriggerConfig";
import GmailActionConfig from "@/components/GmailActionConfig";
import SubWorkflowConfig from "@/components/SubWorkflowConfig";
import AdvancedConditionConfig from "@/components/AdvancedConditionConfig";
import SwitchNodeConfig from "@/components/SwitchNodeConfig";
import AdvancedLoopConfig from "@/components/AdvancedLoopConfig";
import TryCatchConfig from "@/components/TryCatchConfig";
import CommitDialog from "@/components/CommitDialog";
import DebugControlsPanel from "@/components/DebugControlsPanel";
import { toast } from "sonner";
import AICopilot from "@/components/AICopilot";
import CircuitBreakerStatus from "@/components/CircuitBreakerStatus";
import WorkflowNotificationSettings from "@/components/WorkflowNotificationSettings";
import VisualScheduleBuilder from "@/components/VisualScheduleBuilder";
import ScheduleAnalytics from "@/components/ScheduleAnalytics";
import WebhookManager from "@/components/WebhookManager";
import VersionHistoryPanel from "@/components/VersionHistoryPanel";
import ExecutionHistory from "@/components/ExecutionHistory";

// ==================== CUSTOM NODE COMPONENTS ====================

// Base Custom Node with handles
const BaseNode = memo(({ data, type, selected }) => {
  const nodeStyles = {
    start: { bg: 'bg-green-500', icon: Play, label: 'Trigger' },
    action: { bg: 'bg-primary', icon: Zap, label: 'Action' },
    condition: { bg: 'bg-amber-500', icon: GitBranch, label: 'Condition' },
    switch: { bg: 'bg-purple-600', icon: GitMerge, label: 'Switch' },
    delay: { bg: 'bg-blue-500', icon: Clock, label: 'Delay' },
    loop: { bg: 'bg-purple-500', icon: Repeat, label: 'Loop' },
    while: { bg: 'bg-purple-600', icon: RefreshCw, label: 'While' },
    forEach: { bg: 'bg-purple-500', icon: Repeat, label: 'For-Each' },
    errorHandler: { bg: 'bg-red-400', icon: AlertTriangle, label: 'Error Handler' },
    tryCatch: { bg: 'bg-red-500', icon: ShieldAlert, label: 'Try-Catch' },
    ai: { bg: 'bg-gradient-to-br from-violet-500 to-purple-600', icon: Sparkles, label: 'AI' },
    email: { bg: 'bg-emerald-500', icon: Mail, label: 'Email' },
    webhook: { bg: 'bg-orange-500', icon: Globe, label: 'Webhook' },
    database: { bg: 'bg-slate-600', icon: Database, label: 'Database' },
    end: { bg: 'bg-red-500', icon: Square, label: 'End' },
  };

  const style = nodeStyles[type] || nodeStyles.action;
  const Icon = style.icon;

  return (
    <div className={`
      px-4 py-3 shadow-lg rounded-xl bg-card border-2 min-w-[180px] transition-all
      ${selected ? 'border-primary ring-2 ring-primary/30' : 'border-border'}
    `}>
      {/* Input Handle */}
      {type !== 'start' && (
        <Handle
          type="target"
          position={Position.Top}
          className="w-3 h-3 !bg-primary border-2 border-background"
        />
      )}
      
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${style.bg} flex items-center justify-center text-white shadow-sm`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground capitalize">{style.label}</p>
          <p className="text-sm font-medium text-foreground truncate">{data.label}</p>
          {data.config?.description && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{data.config.description}</p>
          )}
        </div>
      </div>

      {/* Show condition badge */}
      {type === 'condition' && data.config?.expression && (
        <div className="mt-2 px-2 py-1 bg-amber-500/10 rounded text-xs font-mono text-amber-600 truncate">
          {data.config.expression}
        </div>
      )}

      {/* Show loop badge */}
      {type === 'loop' && data.config?.maxIterations && (
        <div className="mt-2 flex gap-1">
          <Badge variant="outline" className="text-xs">
            Max: {data.config.maxIterations}
          </Badge>
        </div>
      )}

      {/* Show retry badge */}
      {data.config?.retryPolicy?.enabled && (
        <div className="mt-2">
          <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/30">
            <RefreshCw className="w-3 h-3 mr-1" />
            Retry: {data.config.retryPolicy.maxRetries}x
          </Badge>
        </div>
      )}

      {/* Output Handles */}
      {type !== 'end' && type !== 'condition' && (
        <Handle
          type="source"
          position={Position.Bottom}
          className="w-3 h-3 !bg-primary border-2 border-background"
        />
      )}

      {/* Condition node has two outputs */}
      {type === 'condition' && (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            className="w-3 h-3 !bg-green-500 border-2 border-background !left-[30%]"
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            className="w-3 h-3 !bg-red-500 border-2 border-background !left-[70%]"
          />
          <div className="absolute -bottom-6 left-[30%] -translate-x-1/2 text-xs text-green-500 font-medium">Yes</div>
          <div className="absolute -bottom-6 left-[70%] -translate-x-1/2 text-xs text-red-500 font-medium">No</div>
        </>
      )}
    </div>
  );
});

BaseNode.displayName = 'BaseNode';

// Node types mapping
const nodeTypes = {
  start: (props) => <BaseNode {...props} type="start" />,
  action: (props) => <BaseNode {...props} type="action" />,
  condition: (props) => <BaseNode {...props} type="condition" />,
  switch: (props) => <BaseNode {...props} type="switch" />,
  delay: (props) => <BaseNode {...props} type="delay" />,
  loop: (props) => <BaseNode {...props} type="loop" />,
  while: (props) => <BaseNode {...props} type="loop" />,
  forEach: (props) => <BaseNode {...props} type="loop" />,
  errorHandler: (props) => <BaseNode {...props} type="errorHandler" />,
  tryCatch: (props) => <BaseNode {...props} type="tryCatch" />,
  ai: (props) => <BaseNode {...props} type="ai" />,
  email: (props) => <BaseNode {...props} type="email" />,
  webhook: (props) => <BaseNode {...props} type="webhook" />,
  database: (props) => <BaseNode {...props} type="database" />,
  end: (props) => <BaseNode {...props} type="end" />,
};

// Node type options for dropdown
const NODE_TYPE_OPTIONS = [
  { value: 'start', label: 'Trigger', icon: Play, category: 'flow' },
  { value: 'action', label: 'Action', icon: Zap, category: 'flow' },
  { value: 'condition', label: 'Condition (If/Else)', icon: GitBranch, category: 'logic' },
  { value: 'switch', label: 'Switch (Multi-Branch)', icon: GitMerge, category: 'logic' },
  { value: 'loop', label: 'Loop (For-Each)', icon: Repeat, category: 'logic' },
  { value: 'while', label: 'While Loop', icon: RefreshCw, category: 'logic' },
  { value: 'delay', label: 'Delay (Wait)', icon: Clock, category: 'flow' },
  { value: 'tryCatch', label: 'Try-Catch Block', icon: ShieldAlert, category: 'logic' },
  { value: 'errorHandler', label: 'Error Handler', icon: AlertTriangle, category: 'logic' },
  { value: 'ai', label: 'AI Action', icon: Sparkles, category: 'integration' },
  { value: 'gmailTrigger', label: 'Gmail: New Email', icon: Mail, category: 'integration' },
  { value: 'gmailAction', label: 'Gmail: Send Email', icon: Mail, category: 'integration' },
  { value: 'email', label: 'Send Email', icon: Mail, category: 'integration' },
  { value: 'webhook', label: 'HTTP Request', icon: Globe, category: 'integration' },
  { value: 'database', label: 'Database', icon: Database, category: 'integration' },
  { value: 'getValue', label: 'Get Saved Value', icon: Database, category: 'data' },
  { value: 'setValue', label: 'Save Value', icon: Save, category: 'data' },
  { value: 'subWorkflow', label: 'Run Sub-Workflow', icon: GitBranch, category: 'flow' },
  { value: 'end', label: 'End', icon: Square, category: 'flow' },
];

// ==================== MAIN COMPONENT ====================

export default function WorkflowBuilderPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [workflowName, setWorkflowName] = useState("Untitled Workflow");
  const [workflowDescription, setWorkflowDescription] = useState("");
  const [workflowStatus, setWorkflowStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  
  // Modal states
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testData, setTestData] = useState("{}");
  const [testRunning, setTestRunning] = useState(false);
  
  // Node creation/editing
  const [newNodeType, setNewNodeType] = useState("action");
  const [newNodeLabel, setNewNodeLabel] = useState("");
  const [newNodeConfig, setNewNodeConfig] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  
  // AI suggestions
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Schedule state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleCron, setScheduleCron] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("");
  const [schedulePresets, setSchedulePresets] = useState({});
  
  // Smart App Recommendations
  const [appRecommendations, setAppRecommendations] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(true);

  // AI Copilot state
  const [copilotVisible, setCopilotVisible] = useState(true);

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  
  // Auto-save state
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Fetch schedule presets
  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const response = await axios.get(`${API}/schedule-presets`);
        setSchedulePresets(response.data);
      } catch (error) {
        console.error('Error fetching schedule presets:', error);
      }
    };
    fetchPresets();
  }, []);

  // Fetch app recommendations when nodes change
  const fetchAppRecommendations = useCallback(async () => {
    if (nodes.length === 0) return;
    
    setRecommendationsLoading(true);
    try {
      // Get trigger type from start node
      const startNode = nodes.find(n => n.type === 'start');
      const triggerType = startNode?.data?.label?.toLowerCase() || 'workflow';
      
      // Get existing node types
      const existingNodes = nodes
        .filter(n => n.type !== 'start' && n.type !== 'end')
        .map(n => n.data?.label || n.type);
      
      const response = await axios.post(`${API}/workflows/recommend-apps`, {
        trigger_type: triggerType,
        existing_nodes: existingNodes,
        user_industry: 'general',
        node_position: nodes.length
      }, { withCredentials: true });
      
      setAppRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Error fetching app recommendations:', error);
      // Set default recommendations on error
      setAppRecommendations([
        { app: 'Gmail', rank: 1, confidence: 85, complexity: 'Easy', why: 'Send email notifications' },
        { app: 'Slack', rank: 2, confidence: 83, complexity: 'Easy', why: 'Team notifications' },
        { app: 'OpenAI', rank: 3, confidence: 80, complexity: 'Medium', why: 'AI processing' },
      ]);
    } finally {
      setRecommendationsLoading(false);
    }
  }, [nodes]);

  // Fetch recommendations when modal opens or nodes change significantly
  useEffect(() => {
    if (showNodeModal && nodes.length > 0) {
      fetchAppRecommendations();
    }
  }, [showNodeModal, fetchAppRecommendations]);

  // Load existing workflow
  useEffect(() => {
    if (isEdit) {
      const fetchWorkflow = async () => {
        try {
          const response = await axios.get(`${API}/workflows/${id}`, { withCredentials: true });
          const workflow = response.data;
          setWorkflowName(workflow.name);
          setWorkflowDescription(workflow.description || "");
          setWorkflowStatus(workflow.status);
          setScheduleEnabled(workflow.schedule_enabled || false);
          setScheduleCron(workflow.schedule_cron || "");
          
          if (workflow.schedule_cron) {
            const matchedPreset = Object.entries(schedulePresets).find(
              ([key, preset]) => preset.cron === workflow.schedule_cron
            );
            setSelectedPreset(matchedPreset ? matchedPreset[0] : "custom");
          }
          
          const flowNodes = (workflow.nodes || []).map(node => ({
            id: node.id,
            type: node.type || 'action',
            position: node.position || { x: 250, y: 100 },
            data: { 
              label: node.data?.label || node.label || 'Node',
              config: node.data?.config || node.config || {}
            },
          }));
          
          const flowEdges = (workflow.edges || []).map(edge => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            label: edge.label,
            animated: true,
            style: { stroke: edge.sourceHandle === 'false' ? '#ef4444' : '#32B8C6' },
            markerEnd: { type: MarkerType.ArrowClosed, color: edge.sourceHandle === 'false' ? '#ef4444' : '#32B8C6' },
          }));
          
          setNodes(flowNodes);
          setEdges(flowEdges);
        } catch (error) {
          console.error('Error fetching workflow:', error);
          toast.error("Failed to load workflow");
          navigate('/workflows');
        }
      };
      fetchWorkflow();
    } else {
      // Default nodes for new workflow
      setNodes([
        {
          id: '1',
          type: 'start',
          position: { x: 250, y: 50 },
          data: { label: 'Start', config: {} },
        },
        {
          id: '2',
          type: 'end',
          position: { x: 250, y: 350 },
          data: { label: 'End', config: {} },
        },
      ]);
      setEdges([
        {
          id: 'e1-2',
          source: '1',
          target: '2',
          animated: true,
          style: { stroke: '#32B8C6' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#32B8C6' },
        },
      ]);
    }
  }, [id, isEdit, navigate, setNodes, setEdges, schedulePresets]);

  const onConnect = useCallback(
    (params) => {
      const isConditionFalse = params.sourceHandle === 'false';
      setEdges((eds) => addEdge({ 
        ...params, 
        animated: true,
        style: { stroke: isConditionFalse ? '#ef4444' : '#32B8C6' },
        markerEnd: { type: MarkerType.ArrowClosed, color: isConditionFalse ? '#ef4444' : '#32B8C6' },
      }, eds));
    },
    [setEdges]
  );

  // Handle edge deletion (when user presses Delete/Backspace on selected edge)
  const onEdgesDelete = useCallback(
    (edgesToDelete) => {
      setEdges((eds) => eds.filter((edge) => !edgesToDelete.find((e) => e.id === edge.id)));
      toast.success(`Deleted ${edgesToDelete.length} connection(s)`);
    },
    [setEdges]
  );

  // Handle node deletion (when user presses Delete/Backspace on selected node)
  const onNodesDelete = useCallback(
    (nodesToDelete) => {
      // Prevent deletion of start and end nodes
      const protectedNodes = nodesToDelete.filter(node => node.type === 'start' || node.type === 'end');
      const deletableNodes = nodesToDelete.filter(node => node.type !== 'start' && node.type !== 'end');
      
      if (protectedNodes.length > 0) {
        toast.error('Cannot delete Start or End nodes');
      }
      
      if (deletableNodes.length > 0) {
        setNodes((nds) => nds.filter((node) => !deletableNodes.find((n) => n.id === node.id)));
        toast.success(`Deleted ${deletableNodes.length} node(s)`);
      }
    },
    [setNodes]
  );

  const handleSave = async (publish = false, createVersion = true) => {
    setSaving(true);
    try {
      const workflowData = {
        name: workflowName,
        description: workflowDescription,
        nodes: nodes.map(n => ({
          id: n.id,
          type: n.type,
          position: n.position,
          data: n.data,
          config: n.data?.config || {},
        })),
        edges: edges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
          label: e.label,
        })),
        status: publish ? 'published' : workflowStatus,
        schedule_enabled: scheduleEnabled,
        schedule_cron: scheduleCron || null,
        create_version: createVersion,
        commit_message: publish ? "Published workflow" : undefined,
      };

      if (isEdit) {
        await axios.put(`${API}/workflows/${id}`, workflowData, { withCredentials: true });
        toast.success(publish ? "Workflow published" : "Workflow saved");
        setLastSaved(new Date());
      } else {
        const response = await axios.post(`${API}/workflows`, workflowData, { withCredentials: true });
        toast.success("Workflow created");
        navigate(`/workflows/${response.data.workflow_id}`);
      }
      
      if (publish) setWorkflowStatus('published');
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error("Failed to save workflow");
    } finally {
      setSaving(false);
    }
  };

  // Auto-save function (soft save)
  const autoSave = useCallback(async () => {
    if (!isEdit || !id) return;
    
    setAutoSaving(true);
    try {
      const workflowData = {
        name: workflowName,
        description: workflowDescription,
        nodes: nodes.map(n => ({
          id: n.id,
          type: n.type,
          position: n.position,
          data: n.data,
          config: n.data?.config || {},
        })),
        edges: edges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle,
          targetHandle: e.targetHandle,
          label: e.label,
        })),
        status: workflowStatus,
        schedule_enabled: scheduleEnabled,
        schedule_cron: scheduleCron || null,
      };

      await axios.post(`${API}/workflows/${id}/soft-save`, workflowData, { withCredentials: true });
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setAutoSaving(false);
    }
  }, [isEdit, id, workflowName, workflowDescription, nodes, edges, workflowStatus, scheduleEnabled, scheduleCron]);

  // Auto-save on changes (debounced)
  useEffect(() => {
    if (!isEdit || nodes.length === 0) return;
    
    const timer = setTimeout(() => {
      autoSave();
    }, 3000); // Auto-save after 3 seconds of inactivity

    return () => clearTimeout(timer);
  }, [nodes, edges, workflowName, workflowDescription, autoSave, isEdit]);

  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    if (presetKey && presetKey !== "custom" && schedulePresets[presetKey]) {
      setScheduleCron(schedulePresets[presetKey].cron);
    } else if (presetKey === "custom") {
      setScheduleCron("");
    }
  };

  const handleAddNode = () => {
    if (!newNodeLabel.trim()) {
      toast.error("Please enter a node label");
      return;
    }

    const newNode = {
      id: `node-${Date.now()}`,
      type: newNodeType,
      position: { x: 250, y: nodes.length * 120 + 50 },
      data: { 
        label: newNodeLabel,
        config: { ...newNodeConfig }
      },
    };

    setNodes((nds) => [...nds, newNode]);
    setShowNodeModal(false);
    setNewNodeLabel("");
    setNewNodeConfig({});
    toast.success(`${newNodeLabel} node added`);
  };

  const handleDeleteNode = () => {
    if (!selectedNode) return;
    if (selectedNode.type === 'start' || selectedNode.type === 'end') {
      toast.error("Cannot delete start or end nodes");
      return;
    }
    setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setSelectedNode(null);
    toast.success("Node deleted");
  };

  const handleConfigureNode = () => {
    if (!selectedNode) return;
    setNewNodeConfig(selectedNode.data?.config || {});
    setShowConfigModal(true);
  };

  const handleSaveNodeConfig = () => {
    if (!selectedNode) return;
    setNodes((nds) => nds.map((n) => 
      n.id === selectedNode.id 
        ? { ...n, data: { ...n.data, config: { ...newNodeConfig } } }
        : n
    ));
    setShowConfigModal(false);
    toast.success("Node configured");
  };

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // AI Suggestions
  const generateAISuggestions = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please describe what you want to automate");
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/workflows/ai-suggest`, {
        prompt: aiPrompt,
        current_nodes: nodes.length,
      }, { withCredentials: true });
      
      setAiSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Error generating AI suggestions:', error);
      // Fallback mock suggestions
      setAiSuggestions([
        {
          name: "Email Processing Flow",
          description: "Automatically process incoming emails and categorize them",
          nodes: [
            { type: 'start', label: 'Email Received' },
            { type: 'ai', label: 'Analyze Content' },
            { type: 'condition', label: 'Is Important?', config: { expression: 'priority == "high"' } },
            { type: 'email', label: 'Send Alert' },
            { type: 'end', label: 'Complete' },
          ]
        },
        {
          name: "Data Validation Pipeline",
          description: "Validate and clean incoming data with retry logic",
          nodes: [
            { type: 'start', label: 'Data Input' },
            { type: 'loop', label: 'Process Each Item', config: { maxIterations: 100 } },
            { type: 'action', label: 'Validate', config: { retryPolicy: { enabled: true, maxRetries: 3 } } },
            { type: 'errorHandler', label: 'Handle Errors' },
            { type: 'end', label: 'Done' },
          ]
        },
      ]);
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestion = (suggestion) => {
    const newNodes = suggestion.nodes.map((node, index) => ({
      id: `ai-${Date.now()}-${index}`,
      type: node.type,
      position: { x: 250, y: index * 120 + 50 },
      data: { 
        label: node.label,
        config: node.config || {}
      },
    }));

    const newEdges = newNodes.slice(0, -1).map((node, index) => ({
      id: `ai-edge-${Date.now()}-${index}`,
      source: node.id,
      target: newNodes[index + 1].id,
      animated: true,
      style: { stroke: '#32B8C6' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#32B8C6' },
    }));

    setNodes(newNodes);
    setEdges(newEdges);
    setWorkflowName(suggestion.name);
    setWorkflowDescription(suggestion.description);
    setShowAISuggestions(false);
    toast.success("AI suggestion applied!");
  };

  const handleExecute = async () => {
    if (workflowStatus !== 'published') {
      toast.error("Please publish the workflow first");
      return;
    }
    try {
      await axios.post(`${API}/workflows/${id}/execute`, {}, { withCredentials: true });
      toast.success("Workflow executed successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute workflow");
    }
  };

  const handleToggleActivation = async (checked) => {
    try {
      const endpoint = checked ? 'activate' : 'deactivate';
      const response = await axios.post(
        `${API}/workflows/${id}/${endpoint}`,
        {},
        { withCredentials: true }
      );
      setWorkflowStatus(response.data.status);
      toast.success(`Workflow ${checked ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${checked ? 'activate' : 'deactivate'} workflow`);
    }
  };

  const handleTestWorkflow = async () => {
    setTestRunning(true);
    try {
      let parsedData = {};
      try {
        parsedData = JSON.parse(testData);
      } catch (e) {
        toast.error("Invalid JSON format in test data");
        setTestRunning(false);
        return;
      }

      const response = await axios.post(
        `${API}/workflows/${id}/test`,
        parsedData,
        { withCredentials: true }
      );
      toast.success("Test execution started! Check History tab for results.");
      setShowTestModal(false);
      setTestData("{}");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to test workflow");
    } finally {
      setTestRunning(false);
    }
  };

  // Render Node Configuration based on type
  const renderNodeConfig = () => {
    if (!selectedNode) return null;
    
    const type = selectedNode.type;
    
    return (
      <div className="space-y-4">
        {/* Start/Trigger Configuration */}
        {type === 'start' && (
          <TriggerNodeConfig
            node={selectedNode}
            onUpdate={(updatedNode) => {
              setNodes((nds) => nds.map((n) => 
                n.id === updatedNode.id ? updatedNode : n
              ));
            }}
          />
        )}

        {/* Condition Configuration */}
        {type === 'condition' && (
          <>
            <div className="space-y-2">
              <Label>Condition Expression</Label>
              <Input
                value={newNodeConfig.expression || ''}
                onChange={(e) => setNewNodeConfig({ ...newNodeConfig, expression: e.target.value })}
                placeholder="e.g., data.status == 'active'"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Use variables like data.field_name, context.user_id
              </p>
            </div>
            <div className="space-y-2">
              <Label>Condition Type</Label>
              <Select 
                value={newNodeConfig.conditionType || 'expression'}
                onValueChange={(v) => setNewNodeConfig({ ...newNodeConfig, conditionType: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expression">Custom Expression</SelectItem>
                  <SelectItem value="equals">Equals</SelectItem>
                  <SelectItem value="contains">Contains</SelectItem>
                  <SelectItem value="greater_than">Greater Than</SelectItem>
                  <SelectItem value="less_than">Less Than</SelectItem>
                  <SelectItem value="is_empty">Is Empty</SelectItem>
                  <SelectItem value="is_not_empty">Is Not Empty</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {/* Loop Configuration */}
        {type === 'loop' && (
          <>
            <div className="space-y-2">
              <Label>Loop Type</Label>
              <Select 
                value={newNodeConfig.loopType || 'forEach'}
                onValueChange={(v) => setNewNodeConfig({ ...newNodeConfig, loopType: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="forEach">For Each (iterate collection)</SelectItem>
                  <SelectItem value="while">While (condition-based)</SelectItem>
                  <SelectItem value="times">Times (fixed count)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Collection Path</Label>
              <Input
                value={newNodeConfig.collectionPath || ''}
                onChange={(e) => setNewNodeConfig({ ...newNodeConfig, collectionPath: e.target.value })}
                placeholder="e.g., data.items"
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>Max Iterations: {newNodeConfig.maxIterations || 100}</Label>
              <Slider
                value={[newNodeConfig.maxIterations || 100]}
                onValueChange={([v]) => setNewNodeConfig({ ...newNodeConfig, maxIterations: v })}
                max={1000}
                min={1}
                step={1}
              />
              <p className="text-xs text-muted-foreground">Safety limit to prevent infinite loops</p>
            </div>
            <div className="space-y-2">
              <Label>Concurrency: {newNodeConfig.concurrency || 1}</Label>
              <Slider
                value={[newNodeConfig.concurrency || 1]}
                onValueChange={([v]) => setNewNodeConfig({ ...newNodeConfig, concurrency: v })}
                max={10}
                min={1}
                step={1}
              />
            </div>
          </>
        )}

        {/* Delay Configuration */}
        {type === 'delay' && (
          <>
            <div className="space-y-2">
              <Label>Delay Duration</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={newNodeConfig.delayValue || 1}
                  onChange={(e) => setNewNodeConfig({ ...newNodeConfig, delayValue: parseInt(e.target.value) })}
                  className="w-24"
                />
                <Select 
                  value={newNodeConfig.delayUnit || 'minutes'}
                  onValueChange={(v) => setNewNodeConfig({ ...newNodeConfig, delayUnit: v })}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seconds">Seconds</SelectItem>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}

        {/* Error Handler Configuration - Advanced */}
        {type === 'errorHandler' && (
          <>
            <div className="space-y-2">
              <Label>Error Types to Catch</Label>
              <Select 
                value={newNodeConfig.errorTypes || 'all'}
                onValueChange={(v) => setNewNodeConfig({ ...newNodeConfig, errorTypes: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Errors</SelectItem>
                  <SelectItem value="network">Network Errors</SelectItem>
                  <SelectItem value="timeout">Timeout Errors</SelectItem>
                  <SelectItem value="rate_limit">Rate Limit Errors</SelectItem>
                  <SelectItem value="auth">Authentication Errors</SelectItem>
                  <SelectItem value="validation">Validation Errors</SelectItem>
                  <SelectItem value="server_error">Server Errors (5xx)</SelectItem>
                  <SelectItem value="custom">Custom Error Pattern</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newNodeConfig.errorTypes === 'custom' && (
              <div className="space-y-2">
                <Label>Custom Error Pattern</Label>
                <Input
                  value={newNodeConfig.customErrorPattern || ''}
                  onChange={(e) => setNewNodeConfig({ ...newNodeConfig, customErrorPattern: e.target.value })}
                  placeholder="e.g., *payment*failed* (supports wildcards)"
                  className="font-mono text-sm"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Fallback Action</Label>
              <Select 
                value={newNodeConfig.fallbackAction || 'log'}
                onValueChange={(v) => setNewNodeConfig({ ...newNodeConfig, fallbackAction: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="log">Log Error Only</SelectItem>
                  <SelectItem value="notify">Send Notification</SelectItem>
                  <SelectItem value="retry_from_start">Retry Workflow from Start</SelectItem>
                  <SelectItem value="skip">Skip Failed Node & Continue</SelectItem>
                  <SelectItem value="branch">Route to Error Branch</SelectItem>
                  <SelectItem value="abort">Abort Workflow</SelectItem>
                  <SelectItem value="fallback_value">Use Fallback Value</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newNodeConfig.fallbackAction === 'fallback_value' && (
              <div className="space-y-2">
                <Label>Fallback Value (JSON)</Label>
                <Textarea
                  value={newNodeConfig.fallbackValue || '{}'}
                  onChange={(e) => setNewNodeConfig({ ...newNodeConfig, fallbackValue: e.target.value })}
                  placeholder='{"status": "skipped", "reason": "error"}'
                  className="font-mono text-sm"
                  rows={3}
                />
              </div>
            )}

            {newNodeConfig.fallbackAction === 'notify' && (
              <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium">Notification Settings</p>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newNodeConfig.notifyEmail || false}
                    onCheckedChange={(checked) => setNewNodeConfig({
                      ...newNodeConfig,
                      notifyEmail: checked
                    })}
                  />
                  <Label className="text-sm">Email Notification</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newNodeConfig.notifySlack || false}
                    onCheckedChange={(checked) => setNewNodeConfig({
                      ...newNodeConfig,
                      notifySlack: checked
                    })}
                  />
                  <Label className="text-sm">Slack Notification</Label>
                </div>
              </div>
            )}

            <div className="pt-3 border-t space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Retry on Specific Errors</Label>
                  <p className="text-xs text-muted-foreground">Retry before triggering fallback</p>
                </div>
                <Switch
                  checked={newNodeConfig.autoRetryEnabled || false}
                  onCheckedChange={(checked) => setNewNodeConfig({
                    ...newNodeConfig,
                    autoRetryEnabled: checked
                  })}
                />
              </div>

              {newNodeConfig.autoRetryEnabled && (
                <>
                  <div className="space-y-2">
                    <Label>Retry Attempts: {newNodeConfig.autoRetryCount || 3}</Label>
                    <Slider
                      value={[newNodeConfig.autoRetryCount || 3]}
                      onValueChange={([v]) => setNewNodeConfig({
                        ...newNodeConfig,
                        autoRetryCount: v
                      })}
                      max={10}
                      min={1}
                      step={1}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Retry Delay: {newNodeConfig.autoRetryDelay || 5}s</Label>
                    <Slider
                      value={[newNodeConfig.autoRetryDelay || 5]}
                      onValueChange={([v]) => setNewNodeConfig({
                        ...newNodeConfig,
                        autoRetryDelay: v
                      })}
                      max={120}
                      min={1}
                      step={1}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label>Error Message Template (optional)</Label>
              <Textarea
                value={newNodeConfig.errorMessageTemplate || ''}
                onChange={(e) => setNewNodeConfig({ ...newNodeConfig, errorMessageTemplate: e.target.value })}
                placeholder="Workflow {{workflow_name}} failed at step {{node_label}}: {{error_message}}"
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Variables: {`{{workflow_name}}, {{node_label}}, {{error_message}}, {{error_type}}`}
              </p>
            </div>
          </>
        )}

        {/* AI Action Configuration */}
        {type === 'ai' && (
          <>
            <div className="space-y-2">
              <Label>AI Model</Label>
              <Select 
                value={newNodeConfig.aiModel || 'gpt-4o'}
                onValueChange={(v) => setNewNodeConfig({ ...newNodeConfig, aiModel: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o">GPT-4o (Recommended)</SelectItem>
                  <SelectItem value="gpt-4o-mini">GPT-4o Mini (Faster)</SelectItem>
                  <SelectItem value="claude-sonnet">Claude Sonnet</SelectItem>
                  <SelectItem value="gemini-flash">Gemini Flash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prompt Template</Label>
              <Textarea
                value={newNodeConfig.promptTemplate || ''}
                onChange={(e) => setNewNodeConfig({ ...newNodeConfig, promptTemplate: e.target.value })}
                placeholder="Enter the AI prompt. Use {{data.field}} for variables."
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label>Output Variable</Label>
              <Input
                value={newNodeConfig.outputVariable || 'ai_response'}
                onChange={(e) => setNewNodeConfig({ ...newNodeConfig, outputVariable: e.target.value })}
                placeholder="Variable name for AI output"
              />
            </div>
          </>
        )}

        {/* Gmail Trigger Configuration */}
        {type === 'gmailTrigger' && (
          <GmailTriggerConfig
            nodeData={selectedNode?.data || {}}
            onUpdate={(updatedData) => {
              if (selectedNode) {
                setNodes((nds) => nds.map((n) => 
                  n.id === selectedNode.id ? { ...n, data: updatedData } : n
                ));
              } else {
                setNewNodeConfig(updatedData.config || {});
                setNewNodeLabel(updatedData.label || 'Gmail: New Email');
              }
            }}
            workflowData={{ nodes, edges }}
          />
        )}

        {/* Gmail Action Configuration */}
        {type === 'gmailAction' && (
          <GmailActionConfig
            nodeData={selectedNode?.data || {}}
            onUpdate={(updatedData) => {
              if (selectedNode) {
                setNodes((nds) => nds.map((n) => 
                  n.id === selectedNode.id ? { ...n, data: updatedData } : n
                ));
              } else {
                setNewNodeConfig(updatedData.config || {});
                setNewNodeLabel(updatedData.label || 'Gmail: Send Email');
              }
            }}
            workflowData={{ nodes, edges }}
          />
        )}

        {/* Email Configuration */}
        {type === 'email' && (
          <>
            <div className="space-y-2">
              <Label>To</Label>
              <Input
                value={newNodeConfig.to || ''}
                onChange={(e) => setNewNodeConfig({ ...newNodeConfig, to: e.target.value })}
                placeholder="recipient@example.com or {{data.email}}"
              />
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={newNodeConfig.subject || ''}
                onChange={(e) => setNewNodeConfig({ ...newNodeConfig, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>
            <div className="space-y-2">
              <Label>Body</Label>
              <Textarea
                value={newNodeConfig.body || ''}
                onChange={(e) => setNewNodeConfig({ ...newNodeConfig, body: e.target.value })}
                placeholder="Email body. Use {{data.field}} for variables."
                rows={4}
              />
            </div>
          </>
        )}

        {/* Webhook/HTTP Configuration */}
        {type === 'webhook' && (
          <>
            <div className="space-y-2">
              <Label>HTTP Method</Label>
              <Select 
                value={newNodeConfig.method || 'POST'}
                onValueChange={(v) => setNewNodeConfig({ ...newNodeConfig, method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                value={newNodeConfig.url || ''}
                onChange={(e) => setNewNodeConfig({ ...newNodeConfig, url: e.target.value })}
                placeholder="https://api.example.com/endpoint"
              />
            </div>
            <div className="space-y-2">
              <Label>Request Body (JSON)</Label>
              <Textarea
                value={newNodeConfig.requestBody || ''}
                onChange={(e) => setNewNodeConfig({ ...newNodeConfig, requestBody: e.target.value })}
                placeholder='{"key": "{{data.value}}"}'
                className="font-mono text-sm"
                rows={4}
              />
            </div>
          </>
        )}

        {/* Get Value Configuration */}
        {type === 'getValue' && (
          <GetValueConfig
            config={newNodeConfig}
            onChange={(updatedConfig) => setNewNodeConfig(updatedConfig)}
          />
        )}

        {/* Set Value Configuration */}
        {type === 'setValue' && (
          <SetValueConfig
            config={newNodeConfig}
            onChange={(updatedConfig) => setNewNodeConfig(updatedConfig)}
          />
        )}

        {/* Sub-Workflow Configuration */}
        {type === 'subWorkflow' && (
          <SubWorkflowConfig
            config={newNodeConfig}
            onChange={(updatedConfig) => setNewNodeConfig(updatedConfig)}
          />
        )}

        {/* Advanced Condition Configuration */}
        {type === 'condition' && (
          <AdvancedConditionConfig
            config={newNodeConfig}
            onChange={(updatedConfig) => setNewNodeConfig(updatedConfig)}
          />
        )}

        {/* Switch Node Configuration */}
        {type === 'switch' && (
          <SwitchNodeConfig
            config={newNodeConfig}
            onChange={(updatedConfig) => setNewNodeConfig(updatedConfig)}
          />
        )}

        {/* Advanced Loop Configuration */}
        {(type === 'loop' || type === 'while' || type === 'forEach') && (
          <AdvancedLoopConfig
            config={newNodeConfig}
            onChange={(updatedConfig) => setNewNodeConfig(updatedConfig)}
          />
        )}

        {/* Try-Catch Configuration */}
        {type === 'tryCatch' && (
          <TryCatchConfig
            config={newNodeConfig}
            onChange={(updatedConfig) => setNewNodeConfig(updatedConfig)}
          />
        )}

        {/* Retry Policy (for action nodes) */}
        {['action', 'ai', 'email', 'webhook', 'database'].includes(type) && (
          <div className="pt-4 border-t space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Retry</Label>
                <p className="text-xs text-muted-foreground">Automatically retry on failure</p>
              </div>
              <Switch
                checked={newNodeConfig.retryPolicy?.enabled || false}
                onCheckedChange={(checked) => setNewNodeConfig({
                  ...newNodeConfig,
                  retryPolicy: { ...newNodeConfig.retryPolicy, enabled: checked }
                })}
              />
            </div>
            
            {newNodeConfig.retryPolicy?.enabled && (
              <>
                <div className="space-y-2">
                  <Label>Max Retries: {newNodeConfig.retryPolicy?.maxRetries || 3}</Label>
                  <Slider
                    value={[newNodeConfig.retryPolicy?.maxRetries || 3]}
                    onValueChange={([v]) => setNewNodeConfig({
                      ...newNodeConfig,
                      retryPolicy: { ...newNodeConfig.retryPolicy, maxRetries: v }
                    })}
                    max={10}
                    min={1}
                    step={1}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Backoff Strategy</Label>
                  <Select 
                    value={newNodeConfig.retryPolicy?.backoff || 'exponential'}
                    onValueChange={(v) => setNewNodeConfig({
                      ...newNodeConfig,
                      retryPolicy: { ...newNodeConfig.retryPolicy, backoff: v }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed (same delay)</SelectItem>
                      <SelectItem value="linear">Linear (increasing)</SelectItem>
                      <SelectItem value="exponential">Exponential (2x each time)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Initial Delay (s)</Label>
                    <Input
                      type="number"
                      value={newNodeConfig.retryPolicy?.initialDelay || 1}
                      onChange={(e) => setNewNodeConfig({
                        ...newNodeConfig,
                        retryPolicy: { ...newNodeConfig.retryPolicy, initialDelay: parseInt(e.target.value) || 1 }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Delay (s)</Label>
                    <Input
                      type="number"
                      value={newNodeConfig.retryPolicy?.maxDelay || 300}
                      onChange={(e) => setNewNodeConfig({
                        ...newNodeConfig,
                        retryPolicy: { ...newNodeConfig.retryPolicy, maxDelay: parseInt(e.target.value) || 300 }
                      })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Retry On Error Types</Label>
                  <div className="flex flex-wrap gap-2">
                    {['network', 'timeout', 'rate_limit', 'server_error'].map((errType) => (
                      <Badge
                        key={errType}
                        variant={newNodeConfig.retryPolicy?.retryOn?.includes(errType) ? "default" : "outline"}
                        className="cursor-pointer capitalize"
                        onClick={() => {
                          const current = newNodeConfig.retryPolicy?.retryOn || ['all'];
                          const updated = current.includes(errType) 
                            ? current.filter(t => t !== errType)
                            : [...current.filter(t => t !== 'all'), errType];
                          setNewNodeConfig({
                            ...newNodeConfig,
                            retryPolicy: { ...newNodeConfig.retryPolicy, retryOn: updated.length ? updated : ['all'] }
                          });
                        }}
                      >
                        {errType.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Click to toggle error types for retry</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Error Handling Options (for all executable nodes) */}
        {['action', 'ai', 'email', 'webhook', 'database', 'condition', 'loop'].includes(type) && (
          <div className="pt-4 border-t space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <Label className="font-medium">Error Handling</Label>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Continue on Error</Label>
                <p className="text-xs text-muted-foreground">Proceed to next step if this fails</p>
              </div>
              <Switch
                checked={newNodeConfig.errorHandling?.continueOnError || false}
                onCheckedChange={(checked) => setNewNodeConfig({
                  ...newNodeConfig,
                  errorHandling: { ...newNodeConfig.errorHandling, continueOnError: checked }
                })}
              />
            </div>

            {newNodeConfig.errorHandling?.continueOnError && (
              <div className="space-y-2">
                <Label>Fallback Value (JSON, optional)</Label>
                <Input
                  value={newNodeConfig.errorHandling?.fallbackValue || ''}
                  onChange={(e) => setNewNodeConfig({
                    ...newNodeConfig,
                    errorHandling: { ...newNodeConfig.errorHandling, fallbackValue: e.target.value }
                  })}
                  placeholder='{"status": "skipped"}'
                  className="font-mono text-sm"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Timeout: {newNodeConfig.errorHandling?.timeout || 300}s</Label>
              <Slider
                value={[newNodeConfig.errorHandling?.timeout || 300]}
                onValueChange={([v]) => setNewNodeConfig({
                  ...newNodeConfig,
                  errorHandling: { ...newNodeConfig.errorHandling, timeout: v }
                })}
                max={600}
                min={5}
                step={5}
              />
              <p className="text-xs text-muted-foreground">Max time before step is considered failed</p>
            </div>
          </div>
        )}

        {/* Description for all nodes */}
        <div className="pt-4 border-t space-y-2">
          <Label>Description (optional)</Label>
          <Textarea
            value={newNodeConfig.description || ''}
            onChange={(e) => setNewNodeConfig({ ...newNodeConfig, description: e.target.value })}
            placeholder="Add a note about what this node does..."
            rows={2}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="workflow-builder-page">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card px-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/workflows')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <Input
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              className="text-lg font-heading font-semibold border-none p-0 h-auto focus-visible:ring-0"
              placeholder="Workflow name"
              data-testid="workflow-name-input"
            />
            <div className="flex items-center gap-2">
              <p className="text-xs text-muted-foreground">
                {nodes.length} nodes • {edges.length} connections
              </p>
              {scheduleEnabled && (
                <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                  <Calendar className="w-3 h-3 mr-1" />
                  Scheduled
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Auto-save indicator */}
          {isEdit && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              {autoSaving ? (
                <>
                  <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : lastSaved ? (
                <>
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  Saved {new Date(lastSaved).toLocaleTimeString()}
                </>
              ) : null}
            </div>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowAISuggestions(true)}
            className="rounded-full bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-500/30 text-violet-600"
          >
            <Sparkles className="w-4 h-4 mr-2" /> AI Assist
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowSettingsModal(true)}
            className="rounded-full"
          >
            <Settings className="w-4 h-4 mr-2" /> Settings
          </Button>
          {isEdit && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowVersionHistory(true)}
              className="rounded-full"
            >
              <History className="w-4 h-4 mr-2" /> History
            </Button>
          )}
          {isEdit && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowTestModal(true)}
              className="rounded-full"
              data-testid="test-btn"
            >
              <Play className="w-4 h-4 mr-2" /> Test
            </Button>
          )}
          {isEdit && (
            <div className="flex items-center gap-2 border rounded-full px-3 py-1.5">
              <span className="text-xs font-medium">
                {workflowStatus === 'active' ? 'Active' : 'Inactive'}
              </span>
              <Switch
                checked={workflowStatus === 'active'}
                onCheckedChange={handleToggleActivation}
                data-testid="activation-toggle"
              />
            </div>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="rounded-full"
            data-testid="save-btn"
          >
            <Save className="w-4 h-4 mr-2" /> Save
          </Button>
          <Button 
            size="sm"
            onClick={() => handleSave(true)}
            disabled={saving}
            className="rounded-full bg-primary hover:bg-primary/90"
            data-testid="publish-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" /> Publish
          </Button>
        </div>
      </header>

      {/* Canvas */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgesDelete={onEdgesDelete}
          onNodesDelete={onNodesDelete}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          fitView
          deleteKeyCode="Delete"
          className="dot-pattern"
          defaultEdgeOptions={{
            animated: true,
            style: { stroke: '#32B8C6' },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#32B8C6' },
          }}
        >
          <Controls className="glass-light rounded-xl" />
          <MiniMap 
            className="glass-light rounded-xl"
            nodeColor={(node) => {
              const colors = {
                start: '#22c55e',
                action: '#32B8C6',
                condition: '#f59e0b',
                delay: '#3b82f6',
                loop: '#a855f7',
                errorHandler: '#f87171',
                ai: '#8b5cf6',
                email: '#10b981',
                webhook: '#f97316',
                database: '#475569',
                end: '#ef4444',
              };
              return colors[node.type] || '#6b7280';
            }}
          />
          <Background variant="dots" gap={24} size={1} color="rgba(50, 184, 198, 0.1)" />
          
          <Panel position="top-left" className="space-y-2">
            <Button 
              onClick={() => setShowNodeModal(true)}
              className="rounded-full shadow-lg"
              data-testid="add-node-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Node
            </Button>
            {selectedNode && (
              <>
                <Button 
                  variant="secondary"
                  onClick={handleConfigureNode}
                  className="rounded-full shadow-lg"
                  data-testid="configure-node-btn"
                >
                  <Settings className="w-4 h-4 mr-2" /> Configure
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleDeleteNode}
                  className="rounded-full shadow-lg"
                  data-testid="delete-node-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </>
            )}
          </Panel>

          {/* Node Type Legend */}
          <Panel position="bottom-left" className="glass-light p-3 rounded-xl">
            <p className="text-xs font-medium text-muted-foreground mb-2">Node Types</p>
            <div className="flex flex-wrap gap-2">
              {[
                { type: 'condition', color: 'bg-amber-500', label: 'Condition' },
                { type: 'loop', color: 'bg-purple-500', label: 'Loop' },
                { type: 'error', color: 'bg-red-400', label: 'Error Handler' },
                { type: 'ai', color: 'bg-violet-500', label: 'AI' },
              ].map((item) => (
                <div key={item.type} className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded ${item.color}`} />
                  <span className="text-xs text-muted-foreground">{item.label}</span>
                </div>
              ))}
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {/* Add Node Modal */}
      <Dialog open={showNodeModal} onOpenChange={setShowNodeModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Node</DialogTitle>
            <DialogDescription>Choose a node type or use AI-powered recommendations</DialogDescription>
          </DialogHeader>
          
          {/* Smart App Recommendations */}
          {showRecommendations && appRecommendations.length > 0 && (
            <div className="mb-4 p-4 bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-xl border border-violet-500/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-500" />
                  <span className="font-medium text-sm">Smart Recommendations</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowRecommendations(false)}
                  className="text-xs h-6"
                >
                  Hide
                </Button>
              </div>
              
              {recommendationsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="w-5 h-5 animate-spin text-violet-500" />
                  <span className="ml-2 text-sm text-muted-foreground">Getting recommendations...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {appRecommendations.slice(0, 3).map((rec) => {
                    // Map app name to node type
                    const appToNodeType = {
                      'gmail': 'email',
                      'slack': 'webhook',
                      'openai': 'ai',
                      'google sheets': 'database',
                      'airtable': 'database',
                      'database': 'database',
                      'webhook': 'webhook',
                      'stripe': 'webhook',
                      'quickbooks': 'webhook',
                      'hubspot': 'webhook',
                      'salesforce': 'webhook',
                      'discord': 'webhook',
                      'twilio': 'webhook',
                      'sendgrid': 'email',
                    };
                    
                    const nodeType = appToNodeType[rec.app.toLowerCase()] || 'action';
                    
                    return (
                      <button
                        key={rec.rank}
                        onClick={() => {
                          setNewNodeType(nodeType);
                          setNewNodeLabel(rec.app);
                        }}
                        className={`w-full p-3 rounded-lg border transition-all text-left hover:border-violet-500/50 hover:bg-violet-500/5 ${
                          newNodeLabel === rec.app ? 'border-violet-500 bg-violet-500/10' : 'border-border'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-500 font-semibold text-sm">
                              #{rec.rank}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{rec.app}</span>
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${
                                    rec.complexity === 'Easy' ? 'text-green-500 border-green-500/30' :
                                    rec.complexity === 'Medium' ? 'text-amber-500 border-amber-500/30' :
                                    'text-red-500 border-red-500/30'
                                  }`}
                                >
                                  {rec.complexity}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">{rec.why}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-12 h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"
                                style={{ width: `${rec.confidence}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground w-8">{rec.confidence}%</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {!showRecommendations && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setShowRecommendations(true);
                fetchAppRecommendations();
              }}
              className="mb-4 text-violet-500"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Show Smart Recommendations
            </Button>
          )}
          
          <Tabs defaultValue="flow" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="flow">Flow</TabsTrigger>
              <TabsTrigger value="logic">Logic</TabsTrigger>
              <TabsTrigger value="integration">Integration</TabsTrigger>
              <TabsTrigger value="data">Data</TabsTrigger>
              <TabsTrigger value="errors">Errors</TabsTrigger>
            </TabsList>
            <TabsContent value="flow" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-2">
                {NODE_TYPE_OPTIONS.filter(o => o.category === 'flow').map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setNewNodeType(option.value)}
                      className={`p-3 rounded-lg border-2 flex items-center gap-3 transition-all ${
                        newNodeType === option.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </TabsContent>
            <TabsContent value="logic" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-2">
                {NODE_TYPE_OPTIONS.filter(o => o.category === 'logic').map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setNewNodeType(option.value)}
                      className={`p-3 rounded-lg border-2 flex items-center gap-3 transition-all ${
                        newNodeType === option.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </TabsContent>
            <TabsContent value="integration" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-2">
                {NODE_TYPE_OPTIONS.filter(o => o.category === 'integration').map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setNewNodeType(option.value)}
                      className={`p-3 rounded-lg border-2 flex items-center gap-3 transition-all ${
                        newNodeType === option.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="data" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-2">
                {NODE_TYPE_OPTIONS.filter(o => o.category === 'data').map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setNewNodeType(option.value)}
                      className={`p-3 rounded-lg border-2 flex items-center gap-3 transition-all ${
                        newNodeType === option.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="errors" className="space-y-4 pt-4">
              <div className="space-y-3">
                <Button 
                  onClick={() => {
                    setNewNodeType('errorHandler');
                    setNewNodeLabel('Error Handler');
                    setShowNodeModal(true);
                  }}
                  variant="outline"
                  className="w-full justify-start text-red-600 border-red-300 hover:bg-red-50"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Error Handler
                </Button>
                
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  <p className="font-semibold mb-2">💡 Error Flow Branching</p>
                  <p>Connect error handler nodes with red dotted lines. When a step fails, execution will branch to the error handler.</p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          <div className="space-y-2 pt-4 border-t">
            <Label>Node Label</Label>
            <Input 
              value={newNodeLabel}
              onChange={(e) => setNewNodeLabel(e.target.value)}
              placeholder="e.g., Send Email, Check Status, Process Items"
              data-testid="node-label-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNodeModal(false)}>Cancel</Button>
            <Button onClick={handleAddNode} data-testid="confirm-add-node-btn">Add Node</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configure Node Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configure: {selectedNode?.data?.label}
            </DialogTitle>
            <DialogDescription>
              Set up the behavior for this {selectedNode?.type} node
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {renderNodeConfig()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancel</Button>
            <Button onClick={handleSaveNodeConfig}>Save Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Suggestions Modal */}
      <Dialog open={showAISuggestions} onOpenChange={setShowAISuggestions}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" />
              AI Workflow Assistant
            </DialogTitle>
            <DialogDescription>
              Describe what you want to automate and let AI suggest workflow designs
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Input
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g., Process incoming emails and categorize by urgency, then notify team..."
                className="flex-1"
              />
              <Button onClick={generateAISuggestions} disabled={aiLoading}>
                {aiLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            {aiSuggestions.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium">Suggested Workflows:</p>
                {aiSuggestions.map((suggestion, index) => (
                  <div 
                    key={index}
                    className="p-4 rounded-xl border border-border hover:border-primary/50 cursor-pointer transition-all"
                    onClick={() => applyAISuggestion(suggestion)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{suggestion.name}</p>
                        <p className="text-sm text-muted-foreground mt-1">{suggestion.description}</p>
                      </div>
                      <Badge variant="outline">{suggestion.nodes?.length || 0} nodes</Badge>
                    </div>
                    <div className="flex gap-1 mt-3 flex-wrap">
                      {suggestion.nodes?.map((node, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {node.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Workflow Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input 
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                placeholder="Describe what this workflow does..."
                rows={3}
              />
            </div>
            
            {/* Schedule Section */}
            <div className="space-y-4 p-4 bg-muted/30 rounded-xl border border-border/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Schedule Workflow</p>
                    <p className="text-sm text-muted-foreground">Run automatically on a schedule</p>
                  </div>
                </div>
                <Switch 
                  checked={scheduleEnabled}
                  onCheckedChange={setScheduleEnabled}
                  data-testid="schedule-toggle"
                />
              </div>
              
              {scheduleEnabled && (
                <div className="space-y-4 pt-4 border-t border-border/50">
                  <div className="space-y-2">
                    <Label>Schedule Preset</Label>
                    <Select value={selectedPreset} onValueChange={handlePresetChange}>
                      <SelectTrigger data-testid="schedule-preset-select">
                        <SelectValue placeholder="Select a schedule..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(schedulePresets).map(([key, preset]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <Timer className="w-4 h-4 text-muted-foreground" />
                              <span>{preset.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4 text-muted-foreground" />
                            <span>Custom Cron Expression</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {selectedPreset && selectedPreset !== "custom" && schedulePresets[selectedPreset] && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {schedulePresets[selectedPreset].description}
                      </p>
                    )}
                  </div>
                  
                  {selectedPreset === "custom" && (
                    <div className="space-y-2">
                      <Label>Cron Expression</Label>
                      <Input 
                        value={scheduleCron}
                        onChange={(e) => setScheduleCron(e.target.value)}
                        placeholder="e.g., 0 9 * * 1-5 (weekdays at 9 AM)"
                        className="font-mono text-sm"
                        data-testid="custom-cron-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Format: minute hour day month day_of_week (UTC timezone)
                      </p>
                    </div>
                  )}
                  
                  {scheduleCron && (
                    <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
                      <Clock className="w-4 h-4 text-primary" />
                      <code className="text-xs text-foreground font-mono">{scheduleCron}</code>
                      <Badge variant="outline" className="ml-auto text-xs">
                        UTC
                      </Badge>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Status</p>
                <p className="text-sm text-muted-foreground">
                  {workflowStatus === 'published' ? 'This workflow is live' : 'This workflow is in draft mode'}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                workflowStatus === 'published' 
                  ? 'bg-green-500/10 text-green-500' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                {workflowStatus}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSettingsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Workflow Modal */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Play className="w-5 h-5" />
              Test Workflow
            </DialogTitle>
            <DialogDescription>
              Test your workflow with sample data before activating it
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Test Data (JSON)</Label>
              <Textarea
                value={testData}
                onChange={(e) => setTestData(e.target.value)}
                placeholder='{\n  "email": {\n    "subject": "Test Email",\n    "from": "test@example.com"\n  }\n}'
                className="font-mono text-xs"
                rows={10}
              />
              <p className="text-xs text-muted-foreground">
                Enter sample data in JSON format to simulate a workflow trigger
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestModal(false)} disabled={testRunning}>
              Cancel
            </Button>
            <Button onClick={handleTestWorkflow} disabled={testRunning}>
              {testRunning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Test
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Right Sidebar with Tabs */}
      <div className="w-96 border-l bg-background overflow-y-auto fixed right-0 top-0 h-screen pt-16" style={{zIndex: 40}}>
        <Tabs defaultValue="nodes" className="w-full h-full">
          <TabsList className="grid w-full grid-cols-5 sticky top-0 z-10 bg-white">
            <TabsTrigger value="nodes" data-testid="sidebar-tab-nodes" className="text-xs">Nodes</TabsTrigger>
            <TabsTrigger value="settings" data-testid="sidebar-tab-settings" className="text-xs">Settings</TabsTrigger>
            <TabsTrigger value="copilot" data-testid="sidebar-tab-ai" className="text-xs">AI</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="sidebar-tab-alerts" className="text-xs">Alerts</TabsTrigger>
            <TabsTrigger value="history" data-testid="sidebar-tab-history" className="text-xs">History</TabsTrigger>
          </TabsList>

          <TabsContent value="nodes" className="p-4">
            <div className="text-sm text-muted-foreground mb-4">
              Click buttons below to add nodes to your workflow
            </div>
            <Button 
              onClick={() => setShowNodeModal(true)}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Node
            </Button>
          </TabsContent>

          <TabsContent value="settings" className="p-4 space-y-4">
            <div className="space-y-4">
              {/* Workflow Name & Description */}
              <div className="space-y-2">
                <Label>Workflow Name</Label>
                <Input
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  placeholder="My Workflow"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e.target.value)}
                  placeholder="What does this workflow do?"
                />
              </div>

              {/* Visual Schedule Builder */}
              <VisualScheduleBuilder
                value={scheduleCron}
                onChange={(cron) => setScheduleCron(cron)}
                workflowId={id}
              />

              {/* Schedule Analytics */}
              {id && (
                <ScheduleAnalytics
                  workflowId={id}
                  isEnabled={scheduleEnabled}
                  onToggle={async (enabled) => {
                    setScheduleEnabled(enabled);
                    // Save immediately
                    try {
                      const API_URL = process.env.REACT_APP_BACKEND_URL;
                      const token = localStorage.getItem('token');
                      await axios.put(
                        `${API_URL}/api/workflows/${id}/schedule/toggle`,
                        { enabled },
                        {
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          }
                        }
                      );
                      toast.success(`Schedule ${enabled ? 'enabled' : 'disabled'}`);
                    } catch (error) {
                      toast.error('Failed to update schedule');
                      setScheduleEnabled(!enabled);
                    }
                  }}
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="copilot" className="mt-0">
            <div className="p-4">
              <p className="text-sm text-muted-foreground">
                AI Copilot is available in the bottom-right corner of the canvas.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="alerts" className="p-4 space-y-4" data-testid="alerts-tab-content">
            {id ? (
              <>
                <div data-testid="notification-settings-widget">
                  <WorkflowNotificationSettings workflowId={id} />
                </div>
                <div data-testid="circuit-breaker-widget">
                  <CircuitBreakerStatus workflowId={id} />
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p className="text-sm">Save your workflow first to configure error notifications and circuit breaker settings.</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="p-4 space-y-4" data-testid="history-tab-content">
            {id ? (
              <ExecutionHistory workflowId={id} />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Save your workflow first to view execution history.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* AI Copilot - Always visible by default */}
      <AICopilot
        nodes={nodes}
        edges={edges}
        selectedNode={selectedNode}
        onAddNode={(nodeConfig) => {
          const newId = `node_${Date.now()}`;
          const newNode = {
            id: newId,
            type: nodeConfig.type || "action",
            position: {
              x: 250 + nodes.length * 50,
              y: 100 + nodes.length * 100
            },
            data: {
              label: nodeConfig.label || "New Step",
              ...nodeConfig.data
            }
          };
          setNodes((nds) => [...nds, newNode]);
          toast.success(`Added: ${newNode.data.label}`);
        }}
        onClose={() => setCopilotVisible(false)}
        visible={copilotVisible}
      />

      {/* Version History Panel */}
      <VersionHistoryPanel
        workflowId={id}
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        onRevert={(updatedWorkflow) => {
          // Reload the workflow data after revert
          setWorkflowName(updatedWorkflow.name);
          setWorkflowDescription(updatedWorkflow.description || "");
          setWorkflowStatus(updatedWorkflow.status);
          setScheduleEnabled(updatedWorkflow.schedule_enabled || false);
          setScheduleCron(updatedWorkflow.schedule_cron || "");
          
          const flowNodes = (updatedWorkflow.nodes || []).map(node => ({
            id: node.id,
            type: node.type || 'action',
            position: node.position || { x: 250, y: 100 },
            data: { 
              label: node.data?.label || node.label || 'Node',
              config: node.data?.config || node.config || {}
            },
          }));
          
          const flowEdges = (updatedWorkflow.edges || []).map(edge => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            label: edge.label,
            animated: true,
            style: { stroke: edge.sourceHandle === 'false' ? '#ef4444' : '#32B8C6' },
            markerEnd: { type: MarkerType.ArrowClosed, color: edge.sourceHandle === 'false' ? '#ef4444' : '#32B8C6' },
          }));
          
          setNodes(flowNodes);
          setEdges(flowEdges);
          setShowVersionHistory(false);
        }}
      />
    </div>
  );
}
